import { DeleteModal, Header, Screen, Text } from '@components'
import { useTheme } from '@react-navigation/native'
import { useLanguage } from '@context'
import { useGetContactsByIds, useGetMemoryById, useDeleteMemory, useDeleteAlbum } from '@hooks'
import React, { useCallback, useState } from 'react'
import { Image, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useScreen } from '@hooks'
import { useFocusEffect } from '@react-navigation/native'
import { useQueryClient } from '@tanstack/react-query'
import { ScrollView } from 'react-native-gesture-handler'
import { ScreenProfileProps } from 'src/navigation/navigator/ProfileNavigator'

export const ShowMemory: React.FC<ScreenProfileProps<'ProfileShowMemory'>> = ({
  route,
  navigation,
}): React.ReactElement => {
  const { memoryId, readonly } = route.params
  const theme = useTheme()
  const { t } = useLanguage()
  const { isMobile } = useScreen()

  const { data: memory, isLoading } = useGetMemoryById(memoryId)
  const { contacts, isLoading: isLoadingContacts } = useGetContactsByIds(memory?.sharedWithAccountIds ?? [])
  const { mutateAsync: deleteMemory, isPending: isDeletingMemory } = useDeleteMemory()
  const { mutateAsync: deleteAlbum, isPending: isDeletingAlbum } = useDeleteAlbum()

  const queryClient = useQueryClient()
  const [confirmVisible, setConfirmVisible] = useState(false)

  useFocusEffect(
    useCallback(() => {
      queryClient.invalidateQueries({ queryKey: ['memory', memoryId] })
    }, [memoryId])
  )

  const isPending = isLoading || isDeletingMemory || isDeletingAlbum

  const goBack = () =>
    navigation.canGoBack() ? navigation.goBack() : navigation.navigate('ProfileHome')

  const handleDeleteMemory = async () => {
    await deleteMemory({ id: memoryId })
    goBack()
  }

  const handleDeleteAlbum = async () => {
    if (!memory?.albumId) return
    await deleteAlbum({ id: memory.albumId })
    goBack()
  }

  const styles = getStyle()

  return (
    <Screen isPending={isPending}>
      <Header
        iconLeft="arrowBack"
        iconColor={theme.colors.secondary}
        onPressLeft={goBack}
        iconRight={readonly ? undefined : 'edit'}
        onPressRight={() => navigation.navigate('ProfileAddMemory', { memoryId })}
        title={t('memoriesForm.memory')}
      />

      {memory && (
        <>
          <ScrollView style={styles.container}>
            <View style={[styles.imageContainer, !isMobile && styles.imageContainerDesktop]}>
              <Image style={styles.memoryImage} source={{ uri: memory.mediaUrl }} resizeMode="contain" />
            </View>
            <View style={{ gap: theme.spacing.xl, padding: theme.spacing.lg }}>
              {memory.description ? <Text type="bodySmall">{memory.description}</Text> : null}
              {memory.sharedWithAccountIds.length > 0 && (
                <View style={styles.contactsList}>
                  <Text type="bodyBold">{t('memoriesForm.identifiedContacts')}:</Text>
                  {!isLoadingContacts && contacts.map((contact) => (
                    <View key={contact.id} style={styles.contactContainer}>
                      <Image source={{ uri: contact.avatarUrl }} style={styles.avatar} />
                      <Text type="bodySmall">{contact.fullName}</Text>
                    </View>
                  ))}
                </View>
              )}
            </View>
          </ScrollView>

          {!readonly && (
            <TouchableOpacity
              style={styles.deleteButton}
              onPress={() => setConfirmVisible(true)}
            >
              <Text type="bodySmall" color={theme.colors.error}>{t('common.delete')}</Text>
            </TouchableOpacity>
          )}
        </>
      )}

      <DeleteModal
        isVisible={confirmVisible}
        setIsVisible={setConfirmVisible}
        title=""
        description={t('memoriesForm.deleteMemoryDescription')}
        deleteLabel={t('memoriesForm.deletePhoto')}
        onDelete={handleDeleteMemory}
        extraActions={memory?.albumId ? [{ label: t('memoriesForm.deleteAlbum'), onPress: handleDeleteAlbum }] : []}
      />
    </Screen>
  )
}

const getStyle = () =>
  StyleSheet.create({
    container: {
      flex: 1,
    },
    imageContainer: {
      width: '100%',
      backgroundColor: '#000',
    },
    imageContainerDesktop: {
      maxWidth: 500,
      alignSelf: 'center',
    },
    memoryImage: {
      width: '100%',
      aspectRatio: 1,
    },
    contactsList: {
      gap: 5,
    },
    contactContainer: {
      marginTop: 10,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
    },
    avatar: {
      width: 25,
      height: 25,
      borderRadius: 13,
    },
    deleteButton: {
      alignSelf: 'flex-end',
      marginVertical: 8,
      marginRight: 16,
      padding: 8,
    },
  })
